import type { QueryClient } from "@tanstack/react-query";

/** A browser-only guard shared by every authentication entry point. */
export function createIdentityCacheGuard(client: QueryClient, restartUI: () => void = () => {}) {
  let identity: string | null | undefined;
  return {
    observe(next: string | null) {
      if (identity === next) return;
      // First observation (page load, or right after a completed reset that
      // already cleared the cache): nothing is known to belong to another
      // account. The SSR-hydrated cache was rendered for this same cookie, and
      // removing queries here would cancel in-flight requests and leave their
      // mounted observers pending forever.
      if (identity === undefined) {
        identity = next;
        return;
      }
      // Removing queries also cancels in-flight work, so an old response cannot
      // repopulate the cache after the next account has signed in.
      client.removeQueries({ predicate: q => !(q.queryKey.length === 2 && q.queryKey[0] === "auth" && q.queryKey[1] === "session") });
      client.getMutationCache().clear();
      identity = next;
      // QueryObserver results and component-local state survive cache removal.
      // Restart the document before rendering a different account's session.
      restartUI();
    },
    reset(completed = false) {
      client.clear();
      if (completed) {
        const hadPrivateUI = identity !== undefined && identity !== null;
        identity = undefined;
        if (hadPrivateUI) restartUI();
      }
    },
  };
}

let browserGuard: ReturnType<typeof createIdentityCacheGuard> | undefined;
export function registerIdentityCache(client: QueryClient, restartUI?: () => void) {
  browserGuard = createIdentityCacheGuard(client, restartUI);
}
export function observeAuthIdentity(userId: string | null) {
  browserGuard?.observe(userId);
}
export function resetAuthCache(completed = false) {
  browserGuard?.reset(completed);
}
